// frontend/src/askos/mentoring/MyMentoringBookingsPage.js
//
// Student's own booked mentoring slots — upcoming and past. Join is only
// enabled inside the call's join window (mirrors the faculty side's
// MyMentoringSlotsPage.js).
import React, { useCallback, useEffect, useState } from 'react';
import { Badge, Button, Card, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { myMentoringBookings, cancelMentoringSlotStudent } from '../../api/mentoringApi';

const STATUS_VARIANT = { open: 'secondary', booked: 'danger', completed: 'success', cancelled: 'dark' };
const JOIN_WINDOW_BEFORE_MS = 5 * 60 * 1000;

const canJoin = (slot) => {
    const now = Date.now();
    return now >= new Date(slot.start_utc).getTime() - JOIN_WINDOW_BEFORE_MS && now <= new Date(slot.end_utc).getTime();
};

const MyMentoringBookingsPage = ({ slug }) => {
    const navigate = useNavigate();
    const [bookings, setBookings] = useState([]);
    const [error, setError] = useState('');

    const load = useCallback(() => {
        myMentoringBookings(slug).then(setBookings).catch(() => setBookings([]));
    }, [slug]);

    useEffect(() => {
        load();
        const interval = setInterval(load, 20000);
        return () => clearInterval(interval);
    }, [load]);

    const handleCancel = async (slotId) => {
        try {
            await cancelMentoringSlotStudent(slug, slotId);
            load();
        } catch (err) {
            setError(err.response?.data?.detail || 'Could not cancel this booking.');
        }
    };

    const now = Date.now();
    const upcoming = bookings.filter(b => b.status === 'booked' && new Date(b.end_utc).getTime() >= now);
    const past = bookings.filter(b => !(b.status === 'booked' && new Date(b.end_utc).getTime() >= now));

    return (
        <div className="my-mentoring-bookings-page p-3">
            <h4 className="fw-bold mb-3">My Mentoring Bookings</h4>
            {error && <Alert variant="danger" className="py-2 small" onClose={() => setError('')} dismissible>{error}</Alert>}

            <h6 className="text-muted">Upcoming</h6>
            {upcoming.length === 0 && <p className="text-muted small">No upcoming bookings.</p>}
            {upcoming.map(b => (
                <Card key={b.id} className="mb-2">
                    <Card.Body className="d-flex justify-content-between align-items-center">
                        <strong>{new Date(b.start_utc).toLocaleString()}</strong>
                        <div className="d-flex align-items-center gap-2">
                            <Badge bg={STATUS_VARIANT[b.status] || 'secondary'} className="text-uppercase">{b.status}</Badge>
                            <Button size="sm" variant="outline-secondary" onClick={() => handleCancel(b.id)}>Cancel</Button>
                            {canJoin(b) && (
                                <Button size="sm" variant="success" onClick={() => navigate(`/samkalp-ias/mentoring/${b.id}/call`)}>Join Call</Button>
                            )}
                        </div>
                    </Card.Body>
                </Card>
            ))}

            <h6 className="text-muted mt-4">Past</h6>
            {past.length === 0 && <p className="text-muted small">No past bookings yet.</p>}
            {past.map(b => (
                <Card key={b.id} className="mb-2">
                    <Card.Body className="d-flex justify-content-between align-items-center">
                        <strong>{new Date(b.start_utc).toLocaleString()}</strong>
                        <Badge bg={STATUS_VARIANT[b.status] || 'secondary'} className="text-uppercase">{b.status}</Badge>
                    </Card.Body>
                </Card>
            ))}
        </div>
    );
};

export default MyMentoringBookingsPage;
